import { IDataTableColumn, parseColumnFlexWeight, parseColumnWidth } from '@srm/ui/data-table';
import { IDataGridColumnDescription, IGridDescriptionResponse } from '@models/response/IGridDescriptionResponse';
import { IGridItem } from '@models/response/IGridResponse';
import { getColumnHeaderFilterStateByFieldDataType } from '@utils/gridHeaderFilter';
import { ValueType } from '@srm/ui/types/ValueType';

/**
 * Схема грида с бэкенда → модель базовой таблицы.
 *
 * Серверные списки описываются одним ответом: колонки, пагинация, фильтры, выбор.
 * Здесь этот ответ переводится в пропы `@atoms/DataTable`, а сама выборка данных
 * идёт через серверный источник (`dataTableServerSource`).
 */

/** Строка серверного грида: значения полей лежат в `fields`. */
export type DataGridRow = IGridItem;

export const rowFieldKey = (field: string | null | undefined): string | undefined =>
    field?.replace(/^fields\./, '');

export const rowValue = (row: DataGridRow, field: string | null | undefined): unknown => {
    const key = rowFieldKey(field);
    return key ? (row.fields as Record<string, unknown> | undefined)?.[key] : undefined;
};

/**
 * Идентификатор колонки внутри DataTable.
 *
 * У колонок с `dxGridCellTemplate` бэкенд вправе не задавать `field`: значение
 * им не требуется, содержимое целиком рисует шаблон. DataTable всё равно нужен
 * строковый уникальный id, поэтому таким колонкам выдаём стабильный id по их
 * позиции в серверной схеме. Колонка без field и без шаблона не имеет данных и
 * отображения — её безопаснее не строить.
 */
export const dataGridColumnId = (column: IDataGridColumnDescription, index: number): string | undefined => {
    const field = column.field as string | null | undefined;
    if (field?.trim()) return field;
    return column.dxGridCellTemplate ? `${TEMPLATE_COLUMN_PREFIX}${index}` : undefined;
};

const TEMPLATE_COLUMN_PREFIX = '__template_';

/** Шаблонная колонка без field: сохранённый вид не хранит её настройки. */
export const isDataGridTemplateColumnId = (id: string): boolean => id.startsWith(TEMPLATE_COLUMN_PREFIX);

/**
 * Служебные поля схемы: колонка действий и распорка ширины. Данными не являются
 * и в таблицу не попадают — действия строки живут отдельным слотом, а ширину
 * раздаёт раскладка.
 */
const SERVICE_FIELDS = new Set(['actionColumn', 'emptyColumn']);

export const isServiceColumn = (column: IDataGridColumnDescription): boolean => SERVICE_FIELDS.has(column.field);

/** Колонки описания → колонки таблицы (без рендеров: их навешивает потребитель). */
export function buildDataGridColumns(columns: IDataGridColumnDescription[]): IDataTableColumn<DataGridRow>[] {
    return columns.flatMap((column, index): IDataTableColumn<DataGridRow>[] => {
        if (isServiceColumn(column)) return [];
        const field = dataGridColumnId(column, index);
        if (!field) return [];
        // ширина приходит строкой («200», «10%»): px — точный трек, проценты —
        // пропорция гибкого трека (fr-вес, как в таблицах форм); раньше «10%»
        // молча отбрасывался и колонка получала типовой дефолт — одинаковые
        // настройки давали разные ширины
        const width = parseColumnWidth(column.width);
        const flexWeight = parseColumnFlexWeight(column.width);
        return [
            {
                field,
                header: column.header,
                accessor: (row: DataGridRow) => rowValue(row, column.field),
                dataType: column.dataType ?? ValueType.Text,
                width: width ?? (flexWeight ? undefined : defaultWidthOf(column)),
                flexWeight,
                // схема ширину не задала — она наша догадка, и при нехватке места
                // такая колонка сжимается, а не выталкивает список в прокрутку
                widthHint: width == null && flexWeight == null,
                minWidth: column.minWidth || undefined,
                alignment: column.alignment ?? 'left',
                allowSorting: column.allowSorting !== false,
                allowFiltering: column.allowFiltering !== false,
                allowHiding: column.showInColumnChooser !== false,
                // воронка в шапке — по тем же типам, что и в DX-гриде (даты и флаги без неё)
                allowHeaderFiltering:
                    column.allowFiltering !== false && getColumnHeaderFilterStateByFieldDataType(column.dataType),
                visible: !column.defaultHide,
            },
        ];
    });
}

/**
 * Ширина колонки по умолчанию, когда схема её не задала.
 *
 * Резиновые колонки (доли свободного места) вели себя не как список DX: при
 * скрытии соседей оставшиеся растягивались, а при сужении одной страдали
 * другие. С собственной шириной место забирает распорка справа (аналог
 * `emptyColumn`), а нехватка ширины включает горизонтальную прокрутку.
 */
const DEFAULT_WIDTH: Partial<Record<ValueType, number>> = {
    [ValueType.Date]: 120,
    [ValueType.DateTime]: 150,
    [ValueType.NoSecDateTime]: 145,
    [ValueType.Time]: 100,
    [ValueType.Integer]: 120,
    [ValueType.Double]: 130,
    [ValueType.Money]: 150,
    [ValueType.Year]: 90,
    [ValueType.Boolean]: 90,
    [ValueType.YesNo]: 90,
    [ValueType.LongText]: 220,
    [ValueType.Url]: 200,
};
const DEFAULT_TEXT_WIDTH = 180;

const defaultWidthOf = (column: IDataGridColumnDescription): number =>
    Math.max(
        column.minWidth || 0,
        column.isStatusColumn ? 150 : (DEFAULT_WIDTH[column.dataType] ?? DEFAULT_TEXT_WIDTH),
    );

/** Стартовая сортировка из схемы (sortIndex + sortOrder). Id уровня — само поле
 *  колонки (индикатор на шапке ищет её по id); серверный селектор sortField
 *  подставляет загрузчик DataGrid при отправке запроса. */
export function buildDataGridSort(columns: IDataGridColumnDescription[]) {
    return columns
        .filter((c) => !!c.field && c.sortIndex !== null && c.sortIndex !== undefined)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((c) => ({
            field: c.field,
            dir: c.sortOrder === 'desc' ? ('desc' as const) : ('asc' as const),
        }));
}

/**
 * Опции таблицы из схемы: пагинация, фильтры, выбор, экспорт.
 *
 * Строку фильтров разрешает `options.allowFiltersRow` — одноимённый блок схемы
 * (`filterRow.visible`) бэкенд не заполняет. Действия строки и групповые по
 * `options` не гейтятся: бэкенд шлёт там нули и в DX-гриде они не читались —
 * доступность действий определяет потребитель (режим просмотра, наличие
 * сервиса действий).
 */
export function buildDataGridOptions(scheme: IGridDescriptionResponse) {
    return {
        columnWidthMode:
            scheme.options?.addOptions?.find((option) => option.key === 'columnWidthMode')?.value === 'fill-last'
                ? ('fill-last' as const)
                : ('preserve' as const),
        pageSize: scheme.paging?.pageSize ?? 20,
        pageSizes: scheme.pager?.pagerSizes?.length ? scheme.pager.pagerSizes : undefined,
        /** Списки без страниц (карта маршрута, журнал ЕИС) показывают всё подряд. */
        paging: scheme.paging?.enabled !== false,
        allowColumnChooser: scheme.options?.allowColumnChooser !== false,
        emptyText: scheme.options?.noDataText || undefined,
        showFilterRow: scheme.options?.allowFiltersRow ?? scheme.filterRow?.visible ?? false,
        selectable: !!scheme.selection?.mode && scheme.selection.mode !== 'none',
        recursiveSelection: !!scheme.selection?.recursive,
        /** «Выбрать все» в шапке — как `selection.allowSelectAll` у DX-грида; по умолчанию есть. */
        showSelectAll: scheme.selection?.allowSelectAll !== false,
        // бэкенд пишет режим в нижнем регистре («allpages»)
        selectAllMode: (String(scheme.selection?.selectAllMode ?? '').toLowerCase() === 'allpages'
            ? 'allPages'
            : 'page') as 'allPages' | 'page',
        showGroupPanel: !!scheme.groupPanel?.visible,
        allowExport: !!scheme.options?.excel_format,
        /** Режим прокрутки из схемы: `virtual`/`infinite` — подгрузка по мере скролла. */
        scrollMode: (scheme as { scrolling?: { mode?: string } }).scrolling?.mode ?? 'standard',
    };
}

/**
 * Индексы полей для запроса данных.
 *
 * Бэкенд отдаёт значения только запрошенных полей, а адресуются они позицией
 * колонки в серверной схеме (с единицы) — включая служебные колонки, которые
 * позицию занимают, но данными не являются. Поле `index` в описании для этого
 * непригодно: бэкенд присылает в нём ноль у всех колонок.
 *
 * Считать нужно и по видимым колонкам, и по скрытым с сортировкой — иначе
 * сортировка по скрытому полю не отработает. Готча EUPDEV-12172: позиция
 * берётся из схемы, а не из состояния таблицы — при переиспользовании грида
 * между папками поиска состояние отдаёт порядок предыдущей схемы, и часть
 * полей приезжает пустой.
 */
export function buildFieldsParam(
    columns: IDataGridColumnDescription[],
    visibleFields: ReadonlySet<string>,
    /** Деревья адресуют поля именами (`fields.A||fields.B`), плоские списки — позициями. */
    mode: 'index' | 'name' = 'index',
): string {
    const parts = new Set<string>();
    columns.forEach((column, position) => {
        if (isServiceColumn(column) || !column.field) return;
        if (visibleFields.has(column.field) || column.sortOrder != null) {
            parts.add(mode === 'name' ? column.field : String(position + 1));
        }
    });
    return [...parts].join(mode === 'name' ? '||' : ',');
}
